import { CaseStatus, CensusState, FunnelStage } from '../../services/caseWorkflowService';

/**
 * Plain-language vocabulary for the case-workflow dashboard.
 *
 * The reader is an AML operations lead, not the person who wrote the flow. The
 * business term is the label; the transition code is, at most, secondary text.
 *
 * Most of what is here exists to stop a specific misreading. Each one is
 * commented with the misreading it prevents, because none of them are obvious
 * and all of them were measured on real project data.
 */

/** Stands in wherever the platform records nothing. Never a zero. */
export const NOT_TRACKED = 'Not yet tracked';

/**
 * What a number counts.
 *
 * Load-bearing, and the reason it is printed on the card rather than hidden in
 * a tooltip. A case is a CUSTOMER under investigation and aggregates every
 * alert raised against them: a measured project held 1,746 alerts under 39 case
 * ids, one of them with 141. "1,746" and "39" side by side with no unit read as
 * a funnel with 98% drop-off at the first step.
 */
export type Grain = 'alerts' | 'cases' | 'records';

export const grainWord = (grain: Grain, value: number): string =>
  `${value.toLocaleString()} ${value === 1 ? grain.replace(/s$/, '') : grain}`;

/** Transition codes, as an operations lead would say them. */
export const TRANSITION_LABELS: Record<string, string> = {
  ALERT_QUEUED: 'Alerts raised',
  ALERT_PRIORITISED: 'Scored and prioritised',
  CONTEXT_ESCALATED: 'Escalated for investigation',
  REPORT_DRAFTED: 'SAR drafted for QA',
  REPORT_AUTO_CLOSED: 'Auto-closed',
  ALERT_CONSOLIDATED: 'Grouped into a prioritised case',
  ALERT_SUPPRESSED: 'Suppressed – no aggregate signal',
  EDD_REFERRED: 'Referred to EDD',
  EDD_STARTED: 'EDD started',
  EDD_AWAITING_APPROVAL: 'EDD awaiting sign-off',
  EDD_COMPLETED: 'EDD closed without SAR',
  EDD_SAR_REFERRED: 'EDD referred for SAR',
  REPORT_WITHDRAWN_AFTER_EDD: 'SAR draft withdrawn after EDD'
};

export const transitionLabel = (code: string | null): string => {
  if (!code) return 'Unattributed';
  return TRANSITION_LABELS[code] || code;
};

/** Workflow states, as the contract labels them, with a local fallback. */
export const stateLabel = (code: string, contractLabel?: string): string =>
  contractLabel || code.replace(/_/g, ' ');

/**
 * States nothing advances a record out of.
 *
 * The reporting flow claims report rows, but the report contract declares no
 * claim_workflow_state, so filing a SAR logs no transition and the record sits
 * at QA_Review for ever. Measured: 14 reports at QA_Review whose orchestration
 * status is already `completed` — the work finished, the case did not move.
 *
 * Such a state is excluded from age colouring, or its oldest-case figure grows
 * without bound and reads as a team falling behind.
 */
export const SINK_STATES = new Set(['QA_Review']);

export const isSinkState = (code: string): boolean => SINK_STATES.has(code);

export const SINK_STATE_CAPTION = 'No transition out of this state is wired yet, so records accumulate here and their age is not a backlog.';

/**
 * Metrics with no transition behind them.
 *
 * These are load-bearing omissions, not gaps to fill with zeroes. Enrichment
 * happens INSIDE L2_Escalation and will arrive later as an `annotation` event;
 * closure-after-QA has no transition wired at all. A 0 on a compliance screen
 * asserts "we checked and it did not happen", which would be false.
 */
export const UNWIRED_METRICS: { label: string; why: string }[] = [
  { label: 'Closed after QA', why: 'No transition is wired for filing a SAR yet, so this cannot be counted.' },
  { label: 'Enriched with context', why: 'Enrichment happens inside L2 Escalation and does not log a transition yet.' }
];

/** The KPI row. Grain is per card, because the first one differs from the rest. */
export interface KpiDefinition {
  label: string;
  transitionCode: string;
  grain: Grain;
  /** Which figure on the stage to read — the reason this table exists. */
  read: 'transitions' | 'cases';
  definition: string;
}

export const KPI_CARDS: KpiDefinition[] = [
  {
    label: 'Alerts raised',
    transitionCode: 'ALERT_QUEUED',
    grain: 'alerts',
    // Rows, not distinct cases. Reading `cases` here would report 39 under the
    // label "Alerts raised" — off by a factor of 45, in the plausible direction.
    read: 'transitions',
    definition: 'Individual alerts written by the generating flow. Counts are cumulative across flow re-runs.'
  },
  {
    label: 'Cases opened',
    transitionCode: 'ALERT_QUEUED',
    grain: 'cases',
    read: 'cases',
    definition: 'Distinct customers with at least one alert. One case aggregates every alert raised against that customer.'
  },
  {
    label: 'Scored and prioritised',
    // Not ALERT_PRIORITISED: a P1 writes no context row and goes straight to the
    // closure report, so that code counts P2–P5 only. ALERT_CONSOLIDATED fires
    // once per case, every tier, when the case record is written.
    transitionCode: 'ALERT_CONSOLIDATED',
    grain: 'cases',
    read: 'cases',
    definition: 'Cases the scoring flow assigned a priority tier to — every tier, including those auto-closed at P1.'
  },
  {
    label: 'Alerts suppressed',
    transitionCode: 'ALERT_SUPPRESSED',
    grain: 'alerts',
    read: 'transitions',
    definition: 'Alerts closed without a case because their customer group had no aggregate signal.'
  },
  {
    label: 'Escalated for investigation',
    transitionCode: 'CONTEXT_ESCALATED',
    grain: 'cases',
    read: 'cases',
    definition: 'Cases taken up for due-diligence enrichment at L2.'
  },
  {
    label: 'Auto-closed',
    transitionCode: 'REPORT_AUTO_CLOSED',
    grain: 'cases',
    read: 'cases',
    definition: 'Cases closed automatically by the prioritisation flow without reaching an analyst.'
  },
  {
    label: 'SAR drafted for QA',
    transitionCode: 'REPORT_DRAFTED',
    grain: 'cases',
    read: 'cases',
    definition: 'Cases with a suspicious activity report drafted and awaiting quality review.'
  }
];

export const readStage = (stages: FunnelStage[], card: KpiDefinition): number | null => {
  const stage = stages.find((s) => s.transitionCode === card.transitionCode);
  if (!stage) return null;
  return card.read === 'transitions' ? stage.transitions : stage.cases;
};

/* --------------------------------------------------------- case status */

/**
 * The two populations a chart can describe.
 *
 * Named for closure, not for attention. The contract's only terminal state is
 * Closed, so "not closed" is the strongest thing this data supports — and
 * QA_Review sits inside it even though nothing transitions a record out of that
 * state yet (see SINK_STATES). Labelling this "Open" or "Awaiting action" would
 * assert, on a compliance screen, that someone still has to act on 14 measured
 * reports whose work is already finished.
 */
export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  all: 'All cases',
  not_closed: 'Not closed'
};

/**
 * What the narrowed population is, said once where it is switched on.
 *
 * Case grain is the load-bearing half: a customer with 140 auto-closed alerts
 * and one context row still at L2 is one not-closed case, not 140 closed ones.
 */
export const NOT_CLOSED_CAPTION =
  'Cases with at least one record still in a non-terminal state. A case is a customer, so it counts here while any ' +
  'alert, context or report row under it is unfinished.';

/** The caveat that stops "not closed" being read as "waiting for somebody". */
export const NOT_CLOSED_SINK_CAVEAT =
  'Includes QA Review, which no transition leads out of yet — a case whose report is already filed still counts as not closed.';

/* ------------------------------------------------------------- captions */

/**
 * Dwell is elapsed wall time, not work time.
 *
 * Nothing dedupes flow re-runs, so a case whose alerts were raised on Monday
 * and scored on Wednesday reports a 45-hour median that measures the gap
 * between two runs of the generating flow, not an analyst's latency.
 */
export const DWELL_CAPTION = 'Elapsed time between steps, not analyst working time — a gap can be the interval between two runs of a flow.';

/** alert_types is multiValued: the bars legitimately exceed the case total. */
export const MULTI_VALUED_CAPTION = 'An alert may carry several — these bars do not sum to the case total.';

/**
 * The values in alert_types are codes this platform derives. The institution's
 * own TMS scenario and rule identifiers live on a different object with a
 * different lifecycle. An investigator who sees "Alert type: STRUCT_01" on the
 * benchmark screen and "Alert type: anomaly" here concludes one is broken.
 */
export const DETECTION_SIGNAL_TOOLTIP = 'Signal codes this platform derives from the transaction data. These are not your TMS scenario or rule identifiers, which live on the alert history.';

/** event_at is when the platform moved a case, and no business date exists. */
export const PROCESSED_AXIS_CAPTION = (tz: string) => `Processed date (${tz}) — when the platform moved the case, not when the transaction occurred.`;

export const RERUN_CAPTION = 'Alert counts are cumulative across flow re-runs: each run writes a fresh alert row per transaction under the same case.';

export const CENSUS_CAPTION = 'Cases sitting in each state right now, each counted once — in its most advanced open state, or Closed when nothing is left open. A state every case has already moved past shows nothing here even though the history records it.';

/**
 * What a census row counts: cases at case grain, rows otherwise.
 *
 * The census is requested at case grain. Reading `records` there would put a
 * customer's 141 alerts under "Triage" as 141 — the misreading the grain exists
 * to stop.
 */
export const censusCount = (row: { cases?: number; records?: number } | null | undefined): number =>
  row ? (row.cases ?? row.records ?? 0) : 0;

/**
 * Why the reconstructed backlog can exceed the current one.
 *
 * Measured on a staging project: 996 alert records in the log, 498 surviving —
 * the generating flow ran twice and its first set of rows was replaced. Both
 * numbers are right for their own question, and unexplained the difference
 * reads as half the backlog disappearing.
 */
export const RETIRED_RECORDS_CAPTION = (n: number) =>
  `Includes ${n.toLocaleString()} ${n === 1 ? 'record' : 'records'} that a later flow run replaced and that are no longer in the case store, so this series runs above the current backlog.`;

export const RECONSTRUCTED_CAPTION = (since: string) =>
  `Rebuilt from the transition log, which starts ${new Date(since).toLocaleString()}. Earlier periods under-report.`;

/** An empty diff is the healthy state and has to read as one. */
/**
 * A window that spans a workflow edit compares two different processes.
 *
 * Averaging across the break silently reports one number for two workflows, so
 * the split is named and the reader is pointed at the segmentation that shows it.
 */
export const MULTI_VERSION_CAPTION = (versions: string[]) =>
  `This window spans ${versions.length} workflow versions (${versions.join(', ')}). The dwell figures average across the change — break the trend down by workflow version to see each separately.`;

export const DIFF_HEALTHY = 'The declared workflow matches what actually ran.';

export const BUILTIN_SCOPE_CAPTION = 'Running on the platform defaults — no workflow document is declared for this project.';

/**
 * Three different empty states that all look like a bug if unhandled.
 */
export const EMPTY_NO_CASES = 'No case activity in this project yet.';
export const EMPTY_CUSTOMER_MANAGED = 'This project’s records carry no case ids, so the case-level funnel cannot be drawn. The stage counts above are still accurate.';
export const EMPTY_PRE_MIGRATION = 'Every transition in this window predates workflow tracking, so it is grouped as unattributed rather than assigned to a step.';

/* ---------------------------------------------------------------- format */

export const hours = (value: number | null): string => {
  if (value === null || value === undefined) return '—';
  if (value < 1) return `${Math.round(value * 60)} min`;
  if (value < 48) return `${value.toFixed(1)} h`;
  return `${(value / 24).toFixed(1)} days`;
};

export const percent = (value: number | null): string =>
  value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;

export const count = (value: number | null): string =>
  value === null || value === undefined ? '—' : value.toLocaleString();

/**
 * Colours a state's pile by age against its OWN distribution.
 *
 * No SLA targets are configured anywhere in the platform, so a fixed threshold
 * here would be inventing policy. A sink state is excluded entirely.
 */
export const ageAccent = (state: CensusState, all: CensusState[]): string => {
  if (isSinkState(state.state)) return '#64748B';
  const ages = all
    .filter((s) => !isSinkState(s.state) && s.p50AgeHours !== null)
    .map((s) => s.p50AgeHours as number);
  if (ages.length < 2 || state.p50AgeHours === null) return '#94A3B8';
  const max = Math.max(...ages);
  const min = Math.min(...ages);
  if (max === min) return '#94A3B8';
  const position = (state.p50AgeHours - min) / (max - min);
  if (position > 0.66) return '#F97316';
  if (position > 0.33) return '#FBBF24';
  return '#10B981';
};
